import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { courseAPI } from '../api/courseApi';
import { getImageUrl } from '../utils/helpers';
import Loader from '../components/Loader';
import PaymentDialog from '../components/PaymentDialog';
import { toast } from 'react-toastify';
import { useAuth } from '../context/AuthContext';

const CourseList = () => {
  const navigate = useNavigate();
  const { userInfo } = useAuth();
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [sortBy, setSortBy] = useState('default');
  const [showPaymentDialog, setShowPaymentDialog] = useState(false);
  const [selectedCourse, setSelectedCourse] = useState(null);

  const updatePurchaseStatus = useCallback(async () => {
    try {
      const userId = userInfo?.user_detail?.id;
      if (!userId) return;
      
      // Fetch purchased courses using getMyPurchased (same as My Purchased page)
      const purchasedResponse = await courseAPI.getMyPurchased(userId);
      console.log('My Purchased courses:', purchasedResponse);
      
      if (purchasedResponse && purchasedResponse.data) {
        const purchasedCourses = purchasedResponse.data;
        
        console.log('Purchased courses list:', purchasedCourses);
        
        if (Array.isArray(purchasedCourses) && purchasedCourses.length > 0) {
          // Merge purchase status with existing courses
          setCourses(prevCourses => {
            return prevCourses.map(course => {
              // Check if this course is in the purchased list (match by ID)
              const purchased = purchasedCourses.find(p => String(p.id) === String(course.id));
              if (purchased) {
                console.log(`✅ Course ${course.id} (${course.course_name || course.sub_name}) is PURCHASED`);
                return { 
                  ...course, 
                  purchased: 'Credit', // Mark as purchased
                  group_url: purchased.group_url || course.group_url,
                  type: purchased.type || course.type,
                  viewType: purchased.viewType || course.viewType
                };
              }
              return course;
            });
          });
        }
      }
    } catch (error) {
      console.error('Error updating purchase status:', error);
      // Don't show error to user, just keep existing courses
    }
  }, [userInfo]);

  const fetchCourses = useCallback(async () => {
    try {
      setLoading(true);
      
      // First, fetch all courses
      const response = await courseAPI.getAllCourses();
      console.log('All courses response:', response);
      
      // Handle API response
      if (response && response.data) {
        let coursesData = response.data;
        console.log('Courses data:', coursesData);
        setCourses(Array.isArray(coursesData) ? coursesData : []);
        
        // If user is logged in, update with purchase status
        const userId = userInfo?.user_detail?.id;
        if (userId) {
          await updatePurchaseStatus();
        }
      }
    } catch (error) {
      console.error('Error fetching courses:', error);
      toast.error('Failed to load courses. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [userInfo, updatePurchaseStatus]);

  useEffect(() => {
    fetchCourses();
  }, [fetchCourses]); // Fetch on mount and when fetchCourses changes

  useEffect(() => {
    // Update purchase status when user changes
    if (courses.length > 0 && userInfo) {
      updatePurchaseStatus();
    }
  }, [userInfo, courses.length, updatePurchaseStatus]);

  const handleBuyNow = (subject) => {
    if (!userInfo) {
      toast.error('Please login to purchase');
      navigate('/login');
      return;
    }
    setSelectedCourse(subject);
    setShowPaymentDialog(true);
  };


  const handlePaymentSuccess = () => {
    setShowPaymentDialog(false);
    setSelectedCourse(null);
    // Refresh courses to update purchase status
    fetchCourses();
    toast.success('Payment successful! Course purchased.');
  };

  const handlePaymentClose = () => {
    setShowPaymentDialog(false);
    setSelectedCourse(null);
  };

  // Extract unique categories
  const categories = useMemo(() => {
    const cats = new Set();
    courses.forEach(course => {
      const category = course.category || course.type || 'Other';
      cats.add(category);
    });
    return ['all', ...Array.from(cats)];
  }, [courses]);

  // Filter and sort courses
  const filteredCourses = useMemo(() => {
    let filtered = courses.filter(course => {
      const courseName = course.course_name || course.sub_name || course.name || '';
      const description = course.description || '';
      const category = course.category || course.type || 'Other';
      
      const matchesSearch = courseName.toLowerCase().includes(searchTerm.toLowerCase()) ||
                           description.toLowerCase().includes(searchTerm.toLowerCase());
      const matchesCategory = selectedCategory === 'all' || category === selectedCategory;
      
      return matchesSearch && matchesCategory;
    });

    // Sort courses
    if (sortBy === 'name') {
      filtered.sort((a, b) => {
        const nameA = (a.course_name || a.sub_name || a.name || '').toLowerCase();
        const nameB = (b.course_name || b.sub_name || b.name || '').toLowerCase();
        return nameA.localeCompare(nameB);
      });
    } else if (sortBy === 'price-low') {
      filtered.sort((a, b) => (parseFloat(a.price) || 0) - (parseFloat(b.price) || 0));
    } else if (sortBy === 'price-high') {
      filtered.sort((a, b) => (parseFloat(b.price) || 0) - (parseFloat(a.price) || 0));
    }

    return filtered;
  }, [courses, searchTerm, selectedCategory, sortBy]);

  if (loading) return <Loader />;

  console.log('CourseList render - showPaymentDialog:', showPaymentDialog, 'selectedCourse:', selectedCourse);

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Page Header with Gradient */}
      <div className="bg-gradient-to-r from-blue-600 via-purple-600 to-pink-600 text-white py-16 sm:py-20">
        <div className="container">
          <div className="max-w-3xl mx-auto text-center">
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-4">
              Explore Our Courses
            </h1>
            <p className="text-lg sm:text-xl text-white/90 mb-8">
              Browse our comprehensive collection of {courses.length} professional courses
            </p>
            
            {/* Search Bar in Header */}
            <div className="max-w-2xl mx-auto">
              <div className="relative">
                <input
                  type="text"
                  placeholder="🔍 Search courses by name or description..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full px-6 py-4 pr-14 border-2 border-white/30 rounded-full bg-white/10 backdrop-blur-sm text-white placeholder-white/70 focus:border-white focus:outline-none focus:bg-white/20 transition text-base"
                />
                {searchTerm && (
                  <button
                    onClick={() => setSearchTerm('')}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-white/70 hover:text-white transition"
                  >
                    <span className="text-2xl">×</span>
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Filters and Sort Section */}
      <div className="bg-white shadow-md sticky top-0 z-40 border-b">
        <div className="container py-4">
          <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between">
            {/* Category Filters */}
            <div className="flex items-center gap-2 overflow-x-auto pb-2 sm:pb-0 w-full sm:w-auto">
              <span className="text-sm font-semibold text-gray-700 whitespace-nowrap">Category:</span>
              <div className="flex gap-2">
                {categories.map((category) => (
                  <button
                    key={category}
                    onClick={() => setSelectedCategory(category)}
                    className={`px-4 py-2 rounded-full text-sm font-medium transition whitespace-nowrap ${
                      selectedCategory === category
                        ? 'bg-blue-600 text-white shadow-md'
                        : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                    }`}
                  >
                    {category === 'all' ? 'All Courses' : category}
                  </button>
                ))}
              </div>
            </div>

            {/* Sort Dropdown */}
            <div className="flex items-center gap-2">
              <span className="text-sm font-semibold text-gray-700 whitespace-nowrap">Sort by:</span>
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                className="px-4 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:border-blue-500 bg-white"
              >
                <option value="default">Default</option>
                <option value="name">Name (A-Z)</option>
                <option value="price-low">Price: Low to High</option>
                <option value="price-high">Price: High to Low</option>
              </select>
            </div>
          </div>
        </div>
      </div>

      {/* Results Info */}
      <div className="bg-gray-100 border-b">
        <div className="container py-3">
          <p className="text-sm text-gray-600">
            Showing <span className="font-bold text-gray-900">{filteredCourses.length}</span> of <span className="font-bold text-gray-900">{courses.length}</span> courses
            {selectedCategory !== 'all' && <span className="text-blue-600"> in "{selectedCategory}"</span>}
            {searchTerm && <span className="text-purple-600"> matching "{searchTerm}"</span>}
          </p>
        </div>
      </div>

      {/* Courses Grid */}
      <div className="py-8 sm:py-12">
        <div className="container">
          {filteredCourses.length > 0 ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
              {filteredCourses.map((course, index) => {
                const isPurchased = course.purchased === 'Credit';
                const courseName = course.course_name || course.sub_name || course.name;
                return (
                  <div
                    key={course.id}
                    className="animate-fade-in"
                    style={{ animationDelay: `${index * 0.05}s` }}
                  >
                    <div
                      className={`bg-white rounded-xl shadow-md hover:shadow-xl transition-all duration-300 overflow-hidden ${
                        isPurchased ? 'cursor-pointer' : 'cursor-default'
                      }`}
                      onClick={() => {
                        if (isPurchased) {
                          navigate(`/subjects/${course.id}`, {
                            state: {
                              type: course.type,
                              id: course.id,
                              subject: courseName,
                              flag: 0,
                              viewType: course.viewType
                            }
                          });
                        }
                      }}
                    >
                      {course.images && (
                        <div className="h-48 overflow-hidden relative">
                          <img 
                            src={getImageUrl(course.images)}
                            alt={courseName}
                            className={`w-full h-full object-cover transition-transform duration-300 ${
                              isPurchased ? 'hover:scale-110' : ''
                            }`}
                            onError={(e) => {
                              e.target.src = 'https://via.placeholder.com/400x300?text=Course+Image';
                            }}
                          />
                          {!isPurchased && (
                            <div className="absolute inset-0 bg-black/30 flex items-center justify-center">
                              <span className="text-white text-5xl">🔒</span>
                            </div>
                          )}
                        </div>
                      )}
                      <div className="p-6">
                        <h3 className="text-xl font-bold text-gray-800 mb-3">
                          {courseName}
                        </h3>
                        {course.description && (
                          <div 
                            className="text-gray-600 text-sm mb-4 line-clamp-2"
                            dangerouslySetInnerHTML={{ 
                              __html: course.description.replace(/(&nbsp|amp|quot|lt|gt|;|<([^>]+)>)/gi, '') 
                            }}
                          />
                        )}
                        
                        {/* Always show buttons on course list (flag !== 0 equivalent) */}
                        <div className="space-y-3">
                          {course.purchased === 'Credit' ? (
                            <div className="flex items-center justify-between">
                              <span className="bg-green-500 text-white px-4 py-2 rounded-full text-sm font-bold">
                                ✓ Purchased
                              </span>
                              <button 
                                onClick={(e) => {
                                  e.stopPropagation();
                                  navigate(`/subjects/${course.id}`, {
                                    state: {
                                      type: course.type,
                                      id: course.id,
                                      subject: courseName,
                                      flag: 0,
                                      viewType: course.viewType
                                    }
                                  });
                                }}
                                className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg font-semibold transition text-sm"
                              >
                                View →
                              </button>
                            </div>
                          ) : (
                            <>
                              {course.price && (
                                <div className="flex items-center gap-2 mb-2">
                                  {course.mrp && (
                                    <span className="text-gray-400 line-through text-sm">
                                      ₹{course.mrp}
                                    </span>
                                  )}
                                  <span className="text-green-600 text-xl font-bold">
                                    ₹{course.price}
                                  </span>
                                </div>
                              )}
                              <div className="flex gap-2">
                                <button 
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleBuyNow(course);
                                  }}
                                  className="flex-1 bg-blue-600 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded-lg transition text-sm"
                                  style={{ position: 'relative', zIndex: 10 }}
                                >
                                  Buy Now
                                </button>
                                <button 
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    navigate(`/course/${course.id}`);
                                  }}
                                  className="flex-1 bg-purple-600 hover:bg-purple-700 text-white font-bold py-2 px-4 rounded-lg transition text-sm"
                                >
                                  Demo
                                </button>
                              </div>
                            </>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="text-center py-16 sm:py-24">
              <div className="text-6xl sm:text-8xl mb-6">🔍</div>
              <h3 className="text-2xl sm:text-3xl font-bold text-gray-700 mb-3">
                No courses found
              </h3>
              <p className="text-gray-500 text-base sm:text-lg mb-8">
                {searchTerm
                  ? 'Try different search terms or '
                  : 'No courses available in this category. '}
                {(searchTerm || selectedCategory !== 'all') && 'adjust your filters'}
              </p>
              <div className="flex gap-3 justify-center flex-wrap">
                {searchTerm && (
                  <button
                    onClick={() => setSearchTerm('')}
                    className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-lg font-semibold transition"
                  >
                    Clear Search
                  </button>
                )}
                {selectedCategory !== 'all' && (
                  <button
                    onClick={() => setSelectedCategory('all')}
                    className="bg-purple-600 hover:bg-purple-700 text-white px-6 py-3 rounded-lg font-semibold transition"
                  >
                    View All Categories
                  </button>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

    {/* Payment Dialog */}
    <PaymentDialog
        isOpen={showPaymentDialog}
        onClose={() => setShowPaymentDialog(false)}
        courseInfo={selectedCourse}
        onPaymentSuccess={handlePaymentSuccess}
      />
    </div>
  );
};

export default CourseList;
